
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isTiersOpen, setIsTiersOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => {
    try {
      return location.pathname === path;
    } catch (error) {
      console.error('Error checking active path:', error);
      return false;
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    if (isMenuOpen) {
      setIsTiersOpen(false);
    }
  };

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <span className="text-2xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">
              CrowdRounds®
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors ${
                isActive('/') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/how-it-works"
              className={`text-sm font-medium transition-colors ${
                isActive('/how-it-works') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              How It Works
            </Link>
            <Link
              to="/pricing"
              className={`text-sm font-medium transition-colors ${
                isActive('/pricing') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Pricing
            </Link>

            {/* Tiers Dropdown */}
            <div className="relative group">
              <button className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors flex items-center gap-1">
                Tiers
                <i className="ri-arrow-down-s-line text-base"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                <div className="py-2">
                  <Link
                    to="/tier1-quick-pulse"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                  >
                    Tier 1 — QuickScan™
                  </Link>
                  <Link
                    to="/tier2-market-readiness"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                  >
                    Tier 2 — Foundation Insight™
                  </Link>
                  <Link
                    to="/tier3-competitive-intelligence"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                  >
                    Tier 3 — Competitive Intelligence™
                  </Link>
                  <Link
                    to="/tier4-success-blueprint"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                  >
                    Tier 4 — Success Blueprint™
                  </Link>
                  <Link
                    to="/tier5-full-success-intelligence"
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-600 transition-colors"
                  >
                    Tier 5 — Full Success Intelligence™
                  </Link>
                </div>
              </div>
            </div>

            <Link
              to="/app-wars"
              className={`text-sm font-medium transition-colors ${
                isActive('/app-wars') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              App Wars
            </Link>
            <Link
              to="/submit-app"
              className={`text-sm font-medium transition-colors ${
                isActive('/submit-app') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Submit Your App
            </Link>
            <Link
              to="/for-investors"
              className={`text-sm font-medium transition-colors ${
                isActive('/for-investors') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              For Investors
            </Link>
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors ${
                isActive('/contact') ? 'text-teal-600' : 'text-gray-700 hover:text-teal-600'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/pricing"
              className="px-6 py-2.5 bg-gradient-to-r from-teal-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all shadow-md hover:shadow-lg whitespace-nowrap"
            >
              Start Evaluation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMenu}
            className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <i className={`${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200">
            <div className="flex flex-col space-y-3">
              <Link
                to="/"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/how-it-works"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                How It Works
              </Link>
              <Link
                to="/pricing"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Pricing
              </Link>

              {/* Mobile Tiers Dropdown */}
              <div>
                <button
                  onClick={() => setIsTiersOpen(!isTiersOpen)}
                  className="w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors flex items-center justify-between"
                  aria-expanded={isTiersOpen}
                  aria-controls="tiers-menu"
                >
                  Tiers
                  <i className={`ri-arrow-${isTiersOpen ? 'up' : 'down'}-s-line text-base`}></i>
                </button>
                {isTiersOpen && (
                  <div id="tiers-menu" className="ml-4 mt-2 space-y-2">
                    <Link
                      to="/tier1-quick-pulse"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsTiersOpen(false);
                      }}
                    >
                      Tier 1 — QuickScan™
                    </Link>
                    <Link
                      to="/tier2-market-readiness"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsTiersOpen(false);
                      }}
                    >
                      Tier 2 — Foundation Insight™
                    </Link>
                    <Link
                      to="/tier3-competitive-intelligence"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsTiersOpen(false);
                      }}
                    >
                      Tier 3 — Competitive Intelligence™
                    </Link>
                    <Link
                      to="/tier4-success-blueprint"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsTiersOpen(false);
                      }}
                    >
                      Tier 4 — Success Blueprint™
                    </Link>
                    <Link
                      to="/tier5-full-success-intelligence"
                      className="block px-4 py-2 text-sm text-gray-600 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                      onClick={() => {
                        setIsMenuOpen(false);
                        setIsTiersOpen(false);
                      }}
                    >
                      Tier 5 — Full Success Intelligence™
                    </Link>
                  </div>
                )}
              </div>

              <Link
                to="/app-wars"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                App Wars
              </Link>
              <Link
                to="/submit-app"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Submit Your App
              </Link>
              <Link
                to="/for-investors"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                For Investors
              </Link>
              <Link
                to="/contact"
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-teal-50 hover:text-teal-600 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>

              {/* Mobile CTA */}
              <Link
                to="/pricing"
                className="mx-4 mt-2 px-6 py-2.5 bg-gradient-to-r from-teal-600 to-blue-600 text-white text-sm font-semibold rounded-lg hover:from-teal-700 hover:to-blue-700 transition-all text-center whitespace-nowrap"
                onClick={() => setIsMenuOpen(false)}
              >
                Start Evaluation
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
